"use client";

import { useState } from "react";
import { useSelector } from "react-redux";
import { Dialog, Modal, XIcon } from "staysure-component-library";

import { useTrustpilotRating } from "@/app/components/cover-type/components/description-section/trust-pilot/trust-pilot.hook";
import { IconButtonWrapper } from "@/app/shared-components/button-wrapper/button-wrapper";
import RichText from "@/app/shared-components/rich-text/rich-text";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import ImageAtom from "@/components/atoms/image/image";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import { BRAND_AVANTI } from "@/constants";
import { variationsForBE } from "@/hooks/api/quote/ptm-exit-modal-details";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useFormatCurrency } from "@/libs/i18n/utils.client";
import { useBrand } from "@/providers/brand-provider";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { PtmExitModalType } from "@/utils/schema/quote-page-schema.utils";

import { useExitIntentTrigger } from "./exit-intent-trigger.hook";
import { useEventTracking } from "./use-event-tracking.hook";

const getVariantContent = (
  modalType: string,
  ptmExitModalContent?: PtmExitModalType
) => {
  const isVariantA = modalType === variationsForBE.variantA;
  return {
    modalTitle: isVariantA
      ? ptmExitModalContent?.modalTitleVariantA
      : ptmExitModalContent?.modalTitleVariantB,
    modalDescription: isVariantA
      ? ptmExitModalContent?.modalDescriptionVariantA
      : ptmExitModalContent?.modalDescriptionVariantB,
    goToPTMBtnLabel: isVariantA
      ? ptmExitModalContent?.goToPTMBtnLabelVariantA
      : ptmExitModalContent?.goToPTMBtnLabelVariantB,
    goToPTMBtnLink: isVariantA
      ? ptmExitModalContent?.goToPTMBtnLinkVariantA
      : ptmExitModalContent?.goToPTMBtnLinkVariantB,
    goToPTMBtnRightImage: isVariantA
      ? ptmExitModalContent?.goToPTMBtnRightImageVariantA
      : ptmExitModalContent?.goToPTMBtnRightImageVariantB,
    cancelModalBtnLabel: isVariantA
      ? ptmExitModalContent?.cancelModalBtnLabelVariantA
      : ptmExitModalContent?.cancelModalBtnLabelVariantB,
    ptmLogo: isVariantA
      ? ptmExitModalContent?.ptmLogoVariantA
      : ptmExitModalContent?.ptmLogoVariantB,
    ptmDescription: isVariantA
      ? ptmExitModalContent?.ptmDescriptionVariantA
      : ptmExitModalContent?.ptmDescriptionVariantB,
    trustpilotLogo: isVariantA
      ? ptmExitModalContent?.trustpilotLogoVariantA
      : ptmExitModalContent?.trustpilotLogoVariantB,
    trustpilotStarRate: isVariantA
      ? ptmExitModalContent?.trustpilotStarRateVariantA
      : ptmExitModalContent?.trustpilotStarRateVariantB,
    trustpilotReviewsCount: isVariantA
      ? ptmExitModalContent?.trustpilotReviewsCountVariantA
      : ptmExitModalContent?.trustpilotReviewsCountVariantB,
  };
};

const createImageObject = (id: string) => ({
  "@id": id,
  "@link": `/dam/${id}.png`,
  "@name": "",
  "@path": "",
  "@type": "",
  "@uuid": "",
});

const getBrandStyles = (
  brandName: string,
  modalType: string,
  isVariantA: boolean
) => {
  const isAvanti = brandName === BRAND_AVANTI;

  const getAvantiModalSize = () => {
    const desktopMaxWidth = isVariantA
      ? "md:max-w-[426px]"
      : "md:max-w-[437px]";
    return `${desktopMaxWidth} max-w-[327px]`;
  };

  const getAvantiBtnSize = () => {
    const desktopMaxWidth = isVariantA
      ? "md:max-w-[314px]"
      : "md:max-w-[325px]";
    return `${desktopMaxWidth} max-w-[279px]`;
  };

  return {
    modalSize: isAvanti
      ? getAvantiModalSize()
      : "md:max-w-[416px] max-w-[332px]",
    modalPadding: isAvanti
      ? "md:p-spacing-xxxl p-spacing-l md:pb-spacing-xl pb-spacing-1"
      : "md:p-spacing-xxxl p-spacing-l",
    btnSize: isAvanti ? getAvantiBtnSize() : `md:max-w-[304px] max-w-[284px]`,
    footerFont: isAvanti ? "font-proxima-nova" : "font-faktum",
    titleLineHeight: isAvanti
      ? "md:leading-[34px] leading-[26px]"
      : "md:leading-[34px] leading-[28px]",
  };
};

export default function PtmExitModal({
  ptmExitModalContent,
}: Readonly<{
  ptmExitModalContent?: PtmExitModalType;
}>) {
  const [{ isShow, modalType }, setModalStatus] = useState({
    isShow: false,
    modalType: "",
  });

  const { isLoading } = useLoading();
  const reviewData = useTrustpilotRating("payingtoomuch.com");
  const formatPercent = useFormatCurrency();
  const isMobile = useMediaQuery("(max-width: 599px)");
  const { triggerOptimizelyEvent, triggerGTMEvent } = useEventTracking();
  const { brandName } = useBrand();
  const { referenceId } = useSelector((state: RootState) => state.quote);

  const content = getVariantContent(modalType, ptmExitModalContent);
  const isVariantA = modalType === variationsForBE.variantA;

  const { modalSize, modalPadding, btnSize, footerFont, titleLineHeight } =
    getBrandStyles(brandName, modalType, isVariantA);

  useExitIntentTrigger({
    onTrigger: (type) => {
      triggerOptimizelyEvent("view_ptm_exit_modal");
      triggerGTMEvent("ecosystem_popup_impression", {
        eco_partner_brand: "payingtoomuch",
      });
      setModalStatus({
        isShow: true,
        modalType: type,
      });
    },
  });

  const onCloseModal = (type: string) => {
    const dismissMethod =
      type === "icon" ? "close_icon" : `continue_with_${brandName}`;

    triggerGTMEvent("ecosystem_popup_dismiss", {
      dismiss_method: dismissMethod,
    });
    triggerOptimizelyEvent("dismiss_ptm_model");
    setModalStatus({ isShow: false, modalType: modalType });
  };

  const onNavigateToPTM = () => {
    triggerGTMEvent("ecosystem_popup_click", {
      eco_partner_brand: "payingtoomuch",
      popup_url: content.goToPTMBtnLink,
    });
    triggerOptimizelyEvent("vist_ptm_via_modal");
    // window.open(
    //   `${content.goToPTMBtnLink}`.replace("<<QUOTE_ID>>", `${referenceId}`),
    //   "_blank"
    // );
    setModalStatus({ isShow: false, modalType: modalType });
  };

  const overlayZIndex = isLoading ? "z-[-1]" : "z-[100]";
  const ptmLogoSize = isMobile
    ? { height: 24.75, width: 175 }
    : { height: 29, width: 205 };
  const trustpilotLogoSize = isMobile
    ? { width: 52.61, height: 12.97 }
    : { width: 87.21, height: 21.62 };
  const trustpilotStarSize = isMobile
    ? { height: 14.41, width: 77.84 }
    : { height: 23.06, width: 121.08 };

  return (
    <div
      data-testid="atm-toggle-bottom-wrapper-test-id"
      className="flex justify-center items-center w-auto h-auto px-spacing-l md:px-0"
    >
      {isShow && (
        <Modal
          onOpenChange={(e: boolean) =>
            setModalStatus({ isShow: e, modalType: modalType })
          }
          isOpen={isShow}
          isDismissable={false}
          modalStyles={{
            top: `50%`,
            left: `50%`,
            transform: `translate(-50%, -50%)`,
          }}
          className={`bg-surface-neutral-option-1 relative rounded-radius-lg ${modalSize}`}
          uderlayClassName={`${overlayZIndex} bg-interactive-overlay-default`}
        >
          <Dialog className=" relative">
            <div className={`w-full ${modalPadding}`}>
              <div className="w-spacing-xxl h-spacing-xxl absolute m-[2px] right-0 top-0 flex items-center justify-center md:w-spacing-xxxl md:h-spacing-xxxl">
                <IconButtonWrapper
                  data-testid="remove-ptm-modal-close-icon"
                  id="remove-ptm-modal-close-icon"
                  hierarchy="tertiary"
                  btnType="icon"
                  icon={
                    <XIcon
                      stroke={
                        selectedColorBrand["interactive-secondary-default"]
                      }
                    />
                  }
                  className="w-[24px] h-[24px] text-[24px] text-text-on-primary "
                  onClick={() => {
                    onCloseModal("icon");
                  }}
                />
              </div>
              <div className="flex flex-col w-full gap-spacing-l">
                <div className="flex flex-col w-full md:gap-spacing-s gap-spacing-xs">
                  <h1
                    className={`text-text-title font-header-font text-left w-full tracking-normal md:text-[31px] text-[23px] ${titleLineHeight}`}
                  >
                    {`${content.modalTitle}`.trim()}
                  </h1>
                  <RichText
                    richText={`${content.modalDescription}`}
                    className="text-left font-body-font leading-[27px] w-full text-[18px] tracking-normal md:text-text-on-primary-default !text-surface-neutral-option-9"
                  />
                </div>
                <div
                  className={`flex flex-col w-full gap-spacing-xs ${btnSize}`}
                >
                  <a
                    href={`${content.goToPTMBtnLink}`.replace(
                      "<<QUOTE_ID>>",
                      `${referenceId}`
                    )}
                    target="_blank"
                    rel="noopener"
                  >
                    <LabelButtonWrapper
                      id="go-to-ptm-btn"
                      data-testid="go-to-ptm-btn"
                      hierarchy="primary"
                      label={`${content.goToPTMBtnLabel}`}
                      className={`w-full text-[18px] font-body-font h-[48px] max-h-[48px] font-[600] leading-[100%] gap-[4px] text-surface-neutral-option-9`}
                      onClick={() => {
                        onNavigateToPTM();
                      }}
                      isRightIcon
                      rightIcon={
                        <ImageAtom
                          altText="PTM Logo"
                          styleClass="ml-[4px]  "
                          image={createImageObject(
                            content.goToPTMBtnRightImage || ""
                          )}
                          height={24}
                          width={24}
                        />
                      }
                    />
                  </a>
                  <LabelButtonWrapper
                    id="remove-ptm-modal-close-btn"
                    data-testid="remove-ptm-modal-close-btn"
                    hierarchy="secondary"
                    label={`${content.cancelModalBtnLabel}`}
                    className={`w-full text-[18px] font-body-font h-[48px] max-h-[48px] font-[600] leading-[100%]`}
                    onClick={() => {
                      onCloseModal("btn");
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="bg-[#F2EEED]">
              <div className="flex flex-col  md:py-spacing-xl py-spacing-l gap-[16px]">
                <div className="flex flex-col items-center text-center gap-[2px] md:px-spacing-xxxl">
                  <ImageAtom
                    altText="PTM Logo"
                    image={createImageObject(content.ptmLogo || "")}
                    height={ptmLogoSize.height}
                    width={ptmLogoSize.width}
                  />
                  <RichText
                    richText={`${content.ptmDescription}`}
                    className="font-source-sans-pro-regular text-[14px] leading-[155%] tracking-[0px] text-center text-[#040C53] w-full"
                  />
                </div>
                <div className="flex flex-row justify-center md:gap-[11px] gap-[5.77px]">
                  <ImageAtom
                    altText="Trustpilot Logo"
                    image={createImageObject(content.trustpilotLogo || "")}
                    width={trustpilotLogoSize.width}
                    height={trustpilotLogoSize.height}
                  />
                  <ImageAtom
                    altText="Trustpilot StarRate"
                    image={createImageObject(content.trustpilotStarRate || "")}
                    height={trustpilotStarSize.height}
                    width={trustpilotStarSize.width}
                  />
                  <div className="flex items-center justify-center">
                    <RichText
                      richText={`${content.trustpilotReviewsCount}`.replace(
                        "XXX",
                        String(
                          formatPercent(Number(reviewData.totalReviews))
                            .replace("£", "")
                            .replace(".00", "")
                        )
                      )}
                      className="font-helvetica font-normal tracking-[0] text-center text-text-on-primary-default md:text-[10.09px] text-[7.21px]  md:leading-[14.41px] leading-[11.53px]"
                    />
                  </div>
                </div>
              </div>
              {ptmExitModalContent?.modalSecondDescriptionVariantA &&
                modalType === variationsForBE.variantA && (
                  <div className="border-t border-[#D2CECA] px-spacing-l py-spacing-m">
                    <RichText
                      richText={`${ptmExitModalContent?.modalSecondDescriptionVariantA}`}
                      className={`text-[14px] text-text-on-primary-default text-left leading-[21px] w-full ${footerFont}`}
                    />
                  </div>
                )}
            </div>
          </Dialog>
        </Modal>
      )}
    </div>
  );
}
